import React from 'react';

export type ConfirmationDialogProps = {
    id?: string;
    isOpen: boolean;
    title: string;
    message?: string | React.ReactNode;
    secondaryMessage?: string;
    confirmText?: string;
    cancelText?: string;
    confirmButtonClass?: string;
    onConfirm: () => void;
    onCancel: () => void;
    isLoading?: boolean;
}

export const ConfirmationDialog = ({ id, isOpen, title, message, secondaryMessage = '', confirmText = 'Delete', cancelText = 'Cancel', confirmButtonClass = 'tw-bg-accent-bold hover:tw-bg-accent-bold-hover tw-text-ink-inverse', onConfirm, onCancel, isLoading = false }: ConfirmationDialogProps) => {
    const baseId = id || 'confirmation-dialog';
    if (!isOpen) return null;

    const handleConfirm = () => {
        if (!isLoading) {
            onConfirm();
        }
    };

    const handleCancel = () => {
        if (!isLoading) {
            onCancel();
        }
    };

    return (
        <div id={`${baseId}-overlay`} className="tw-fixed tw-inset-0 tw-flex tw-items-center tw-justify-center tw-bg-grey-900 tw-bg-opacity-50 tw-backdrop-blur-sm tw-z-50">
            <div id={baseId} data-testid={baseId} className="tw-bg-surface-raised tw-rounded tw-shadow-ds-md tw-w-96 tw-max-w-[90%]">
                <div id={`${baseId}-header`} className='tw-border-b tw-border-edge tw-px-6 tw-pt-4'>
                    <h3 id={`${baseId}-title`} className="tw-text-base tw-font-semibold tw-mb-2 tw-text-ink ">
                        {title}
                    </h3>
                </div>

                <p id={`${baseId}-message`} className="tw-text-ink tw-text-sm tw-font-medium tw-mb-2 tw-px-6 tw-pt-2">
                    {message}
                </p>
                {secondaryMessage ? <p id={`${baseId}-secondary-message`} className="tw-text-ink-secondary tw-text-sm tw-font-medium tw-px-6 tw-mb-6">
                    {secondaryMessage}
                </p> : null}

                <div id={`${baseId}-actions`} className="tw-flex tw-justify-end tw-space-x-3 tw-px-6 tw-py-2">
                    <button
                        id={`${baseId}-cancel-btn`}
                        data-testid={`${baseId}-cancel-btn`}
                        onClick={handleCancel}
                        disabled={isLoading}
                        className="tw-px-4 tw-py-2 tw-border tw-border-edge-strong tw-rounded tw-text-ink hover:tw-bg-surface-hover disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-transition-colors"
                    >
                        {cancelText}
                    </button>

                    <button
                        id={`${baseId}-confirm-btn`}
                        data-testid={`${baseId}-confirm-btn`}
                        onClick={handleConfirm}
                        disabled={isLoading}
                        className={`tw-px-4 tw-py-2 tw-rounded tw-font-medium disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-transition-colors ${confirmButtonClass}`}
                    >
                        {isLoading ? (
                            <span id={`${baseId}-loading-indicator`} className="tw-flex tw-items-center">
                                <svg id={`${baseId}-spinner`} className="tw-animate-spin tw-h-4 tw-w-4 tw-mr-2" viewBox="0 0 24 24">
                                    <circle
                                        className="tw-opacity-25"
                                        cx="12"
                                        cy="12"
                                        r="10"
                                        stroke="currentColor"
                                        strokeWidth="4"
                                        fill="none"
                                    />
                                    <path
                                        className="tw-opacity-75"
                                        fill="currentColor"
                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                                    />
                                </svg>
                                Processing...
                            </span>
                        ) : (
                            confirmText
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};